/**
 * BellvileDental Calendar Page
 * Dental clinic appointment scheduling calendar
 *
 * Reuses GoldenWings calendar components with Bellville Dental configuration.
 */

import React, { useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  message,
  Spin,
  Alert,
  Button,
  Select,
  DatePicker,
  Space,
  Badge,
  Tooltip,
} from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLayoutConfig } from '@/components/common/ProtectedLayout';
import { MultiDayVerticalView } from '@/components/features/goldenwings/calendar/MultiDayVerticalView';
import { MonthlyView } from '@/components/features/goldenwings/calendar/MonthlyView';
import { YearView } from '@/components/features/goldenwings/calendar/YearView';
import { YearlyAgendaView } from '@/components/features/goldenwings/calendar/YearlyAgendaView';
import { HorizontalView } from '@/components/features/goldenwings/calendar/HorizontalView';
import { GenericResourceFilterPanel } from '@/components/features/calendar/GenericResourceFilterPanel';
import type { FilterGroupCollapsedState } from '@/components/features/calendar/GenericResourceFilterPanel';
import { calculateMondayOffset } from '@/components/features/goldenwings/calendar/utils/multiDayHelpers';
import { useCalendars, useEvents, useResources } from '@/hooks/useCalendars';
import { useCalendarSocket } from '@/hooks/useCalendarSocket';
import { calendarsService } from '@/api/services/calendars.service';
import type { CalendarEvent, Resource } from '@/api/types/calendars.types';
import {
  useCalendarSettings,
  useCalendarFilters,
  useSaveCalendarSettings,
  useSaveCalendarFilters,
} from '@/hooks/useCalendar';
import { bellviledentalConfig } from '@/config/tenants/bellviledental.config';
import './CalendarPage.css';

// Calendar views for Bellville Dental (all views like GoldenWings)
type BellvileDentalView =
  | '1day'
  | '2day'
  | '3day'
  | '7day'
  | 'week-new'
  | 'month'
  | 'year'
  | 'year-agenda'
  | 'day-h-1h'
  | 'day-h-2d'
  | 'day-h-3d'
  | 'week-h';

/**
 * Format the period display based on current view
 */
const formatPeriod = (date: Date, view: BellvileDentalView): string => {
  const d = dayjs(date);

  switch (view) {
    case '1day':
    case 'day-h-1h':
      return d.format('dddd, D. MMMM YYYY');
    case '2day':
    case 'day-h-2d': {
      const end = d.add(1, 'day');
      if (d.month() === end.month()) {
        return `${d.format('D')}-${end.format('D')} ${d.format('MMMM YYYY')}`;
      }
      return `${d.format('D MMM')} - ${end.format('D MMM YYYY')}`;
    }
    case '3day':
    case 'day-h-3d': {
      const start = d.subtract(1, 'day');
      const end = d.add(1, 'day');
      if (start.month() === end.month()) {
        return `${start.format('D')}-${end.format('D')} ${start.format('MMMM YYYY')}`;
      }
      return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`;
    }
    case '7day':
    case 'week-h': {
      const start = d;
      const end = start.add(6, 'day');
      if (start.month() === end.month()) {
        return `${start.format('D')}-${end.format('D')} ${start.format('MMMM YYYY')}`;
      }
      return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`;
    }
    case 'week-new': {
      const dayOfWeek = d.day();
      const mondayBased = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const startOfWeek = d.subtract(mondayBased, 'day');
      const endOfWeek = startOfWeek.add(6, 'day');
      if (startOfWeek.month() === endOfWeek.month()) {
        return `${startOfWeek.format('D')}-${endOfWeek.format('D')} ${startOfWeek.format('MMMM YYYY')}`;
      }
      return `${startOfWeek.format('D MMM')} - ${endOfWeek.format('D MMM YYYY')}`;
    }
    case 'month':
      return d.format('MMMM YYYY');
    case 'year':
    case 'year-agenda':
      return d.format('YYYY');
    default:
      return d.format('MMMM YYYY');
  }
};

/**
 * Generate filter summary tooltip content
 */
const generateFilterSummary = (
  selectedResourceIds: string[],
  allResources: Resource[]
): React.ReactNode => {
  if (selectedResourceIds.length === 0) {
    return <div style={{ padding: '4px 0' }}>Nema selektovanih filtera</div>;
  }

  const selectedResources = allResources.filter((r) =>
    selectedResourceIds.includes(r.id)
  );

  const counts: Record<string, number> = {};
  selectedResources.forEach((resource) => {
    const type = resource.resource_type;
    counts[type] = (counts[type] || 0) + 1;
  });

  const parts: string[] = [];
  if (counts['CONFERENCE_ROOM'])
    parts.push(`🪑 ${counts['CONFERENCE_ROOM']} stolica`);
  if (counts['PERSON']) parts.push(`👨‍⚕️ ${counts['PERSON']} doktor`);

  return (
    <div style={{ padding: '4px 0' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Aktivni filteri ({selectedResourceIds.length})
      </div>
      {parts.map((part, index) => (
        <div key={index} style={{ fontSize: 12 }}>
          {part}
        </div>
      ))}
    </div>
  );
};

export const BellvileDentalCalendarPage: React.FC = () => {
  const config = bellviledentalConfig;
  const queryClient = useQueryClient();
  const { setNoPadding } = useLayoutConfig();

  // Load persistent settings and filters
  const { data: savedSettings, isLoading: loadingSettings } =
    useCalendarSettings();
  const { data: savedFilters, isLoading: loadingFilters } =
    useCalendarFilters();
  const saveSettingsMutation = useSaveCalendarSettings();
  const saveFiltersMutation = useSaveCalendarFilters();

  const [view, setView] = useState<BellvileDentalView>('week-new');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [collapsedGroups, setCollapsedGroups] =
    useState<FilterGroupCollapsedState>({});
  const [initializedFromBackend, setInitializedFromBackend] = useState(false);

  // Set noPadding for calendar page
  useEffect(() => {
    setNoPadding(true);
    return () => setNoPadding(false);
  }, [setNoPadding]);

  // Fetch calendars
  const { data: calendars = [], isLoading: loadingCalendars } = useCalendars();

  // Mutation for updating events (drag & drop)
  const updateEventMutation = useMutation({
    mutationFn: ({
      eventId,
      startTime,
      endTime,
    }: {
      eventId: string;
      startTime: string;
      endTime: string;
    }) =>
      calendarsService.updateEvent(eventId, {
        start_time: startTime,
        end_time: endTime,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendars', 'events'] });
      message.success('Termin je uspešno ažuriran');
    },
    onError: (error: any) => {
      console.error('Error updating event:', error);
      message.error(
        error?.response?.data?.detail || 'Greška pri ažuriranju termina'
      );
      queryClient.invalidateQueries({ queryKey: ['calendars', 'events'] });
    },
  });

  const calendarId = calendars[0]?.id;

  // WebSocket real-time updates
  useCalendarSocket({
    enabled: !!calendarId && config.calendar.features.realTimeUpdates,
    calendarIds: calendarId ? [calendarId] : [],
    selectedResourceIds,
    onEventCreated: (data) => {
      console.log('📅 Novi termin kreiran:', data.event.title);
    },
    onEventUpdated: (data) => {
      console.log('📅 Termin ažuriran:', data.event.title);
    },
    onEventDeleted: (data) => {
      console.log('📅 Termin obrisan:', data.event_id);
    },
  });

  // Calculate date range for event counts
  const eventCountDateRange = useMemo(() => {
    const current = dayjs(currentDate);
    let start: string;
    let end: string;

    switch (view) {
      case '1day':
      case 'day-h-1h':
        start = current.format('YYYY-MM-DD');
        end = current.add(1, 'day').format('YYYY-MM-DD');
        break;
      case '2day':
      case 'day-h-2d':
        start = current.format('YYYY-MM-DD');
        end = current.add(1, 'day').format('YYYY-MM-DD');
        break;
      case '3day':
      case 'day-h-3d':
        start = current.subtract(1, 'day').format('YYYY-MM-DD');
        end = current.add(1, 'day').format('YYYY-MM-DD');
        break;
      case '7day':
      case 'week-h':
        start = current.subtract(1, 'day').format('YYYY-MM-DD');
        end = current.add(5, 'day').format('YYYY-MM-DD');
        break;
      case 'week-new': {
        const dayOfWeek = current.day();
        const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        start = current.subtract(mondayOffset, 'day').format('YYYY-MM-DD');
        end = current
          .subtract(mondayOffset, 'day')
          .add(6, 'day')
          .format('YYYY-MM-DD');
        break;
      }
      case 'month':
        start = current.startOf('month').format('YYYY-MM-DD');
        end = current.endOf('month').format('YYYY-MM-DD');
        break;
      case 'year':
      case 'year-agenda':
        start = current.startOf('year').format('YYYY-MM-DD');
        end = current.endOf('year').format('YYYY-MM-DD');
        break;
      default:
        start = current.startOf('month').format('YYYY-MM-DD');
        end = current.endOf('month').format('YYYY-MM-DD');
    }

    return { start_date: start, end_date: end };
  }, [currentDate, view]);

  // Fetch resources based on tenant config
  const { data: chairs = [] } = useResources({
    resource_type: 'CONFERENCE_ROOM',
    ...eventCountDateRange,
  });
  const { data: doctors = [] } = useResources({
    resource_type: 'PERSON',
    ...eventCountDateRange,
  });

  // Combine all resources
  const allResources = useMemo(
    () => [...chairs, ...doctors],
    [chairs, doctors]
  );

  // Calculate effective resource IDs for filtering
  const effectiveResourceIds = useMemo(() => {
    const result: string[] = [];

    const addGroupIfSubset = (
      allInGroup: typeof chairs,
      selectedIds: string[]
    ) => {
      const groupIds = allInGroup.map((r) => r.id);
      const selectedInGroup = selectedIds.filter((id) => groupIds.includes(id));
      if (
        selectedInGroup.length > 0 &&
        selectedInGroup.length < groupIds.length
      ) {
        result.push(...selectedInGroup);
      }
    };

    addGroupIfSubset(chairs, selectedResourceIds);
    addGroupIfSubset(doctors, selectedResourceIds);

    return result;
  }, [chairs, doctors, selectedResourceIds]);

  // Initialize from backend saved options
  useEffect(() => {
    if (loadingSettings || loadingFilters || initializedFromBackend) {
      return;
    }

    if (savedSettings?.default_view) {
      const validViews = config.calendar.availableViews;
      if (validViews.includes(savedSettings.default_view)) {
        setView(savedSettings.default_view as BellvileDentalView);
      }
    }

    if (savedFilters?.calendar_ids && savedFilters.calendar_ids.length > 0) {
      setSelectedResourceIds(savedFilters.calendar_ids);
    }

    if (savedSettings?.filters_visible !== undefined) {
      setFiltersVisible(savedSettings.filters_visible);
    }

    if (savedSettings?.collapsed_groups) {
      setCollapsedGroups(savedSettings.collapsed_groups);
    }

    setInitializedFromBackend(true);
  }, [
    savedSettings,
    savedFilters,
    loadingSettings,
    loadingFilters,
    initializedFromBackend,
    config.calendar.availableViews,
  ]);

  // Calculate date range based on view
  const dateRange = useMemo(() => {
    const current = dayjs(currentDate);
    let start: string;
    let end: string;

    switch (view) {
      case '1day':
      case 'day-h-1h':
        start = current.startOf('day').toISOString();
        end = current.add(1, 'day').startOf('day').toISOString();
        break;
      case '2day':
        start = current.startOf('day').toISOString();
        end = current.add(1, 'day').endOf('day').toISOString();
        break;
      case 'day-h-2d':
        start = current.startOf('day').toISOString();
        end = current.add(1, 'day').endOf('day').toISOString();
        break;
      case '3day':
        start = current.subtract(1, 'day').startOf('day').toISOString();
        end = current.add(1, 'day').endOf('day').toISOString();
        break;
      case 'day-h-3d':
        start = current.startOf('day').toISOString();
        end = current.add(2, 'day').endOf('day').toISOString();
        break;
      case '7day':
        start = current.subtract(1, 'day').startOf('day').toISOString();
        end = current.add(5, 'day').endOf('day').toISOString();
        break;
      case 'week-h':
        start = current.startOf('day').toISOString();
        end = current.add(6, 'day').endOf('day').toISOString();
        break;
      case 'week-new': {
        const dayOfWeek = current.day();
        const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        start = current
          .subtract(mondayOffset, 'day')
          .startOf('day')
          .toISOString();
        end = current
          .subtract(mondayOffset, 'day')
          .add(6, 'day')
          .endOf('day')
          .toISOString();
        break;
      }
      case 'month':
        start = current.startOf('month').subtract(7, 'days').toISOString();
        end = current.endOf('month').add(7, 'days').toISOString();
        break;
      default:
        start = current.startOf('month').toISOString();
        end = current.endOf('month').toISOString();
    }

    return { start_date: start, end_date: end };
  }, [currentDate, view]);

  // Fetch events for the calendar with filters
  const {
    data: events = [],
    isLoading: loadingEvents,
    error: eventsError,
  } = useEvents(calendarId || '', {
    ...dateRange,
    resource_ids:
      effectiveResourceIds.length > 0 ? effectiveResourceIds : undefined,
  });

  // Fetch ALL events for cross-filter count calculation
  const { data: allEventsForCounting = [] } = useEvents(
    filtersVisible ? calendarId || '' : '',
    { ...dateRange }
  );

  const loading = loadingCalendars || loadingEvents;

  // Navigation handlers
  const handlePrevious = () => {
    const newDate = dayjs(currentDate);
    switch (view) {
      case '1day':
      case 'day-h-1h':
        setCurrentDate(newDate.subtract(1, 'day').toDate());
        break;
      case '2day':
      case 'day-h-2d':
        setCurrentDate(newDate.subtract(2, 'day').toDate());
        break;
      case '3day':
      case 'day-h-3d':
        setCurrentDate(newDate.subtract(3, 'day').toDate());
        break;
      case '7day':
      case 'week-h':
        setCurrentDate(newDate.subtract(7, 'day').toDate());
        break;
      case 'week-new':
        setCurrentDate(newDate.subtract(1, 'week').toDate());
        break;
      case 'month':
        setCurrentDate(newDate.subtract(1, 'month').toDate());
        break;
      case 'year':
      case 'year-agenda':
        setCurrentDate(newDate.subtract(1, 'year').toDate());
        break;
    }
  };

  const handleNext = () => {
    const newDate = dayjs(currentDate);
    switch (view) {
      case '1day':
      case 'day-h-1h':
        setCurrentDate(newDate.add(1, 'day').toDate());
        break;
      case '2day':
      case 'day-h-2d':
        setCurrentDate(newDate.add(2, 'day').toDate());
        break;
      case '3day':
      case 'day-h-3d':
        setCurrentDate(newDate.add(3, 'day').toDate());
        break;
      case '7day':
      case 'week-h':
        setCurrentDate(newDate.add(7, 'day').toDate());
        break;
      case 'week-new':
        setCurrentDate(newDate.add(1, 'week').toDate());
        break;
      case 'month':
        setCurrentDate(newDate.add(1, 'month').toDate());
        break;
      case 'year':
      case 'year-agenda':
        setCurrentDate(newDate.add(1, 'year').toDate());
        break;
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleToggleFilters = () => {
    const newValue = !filtersVisible;
    setFiltersVisible(newValue);
    saveSettingsMutation.mutate({
      ...savedSettings,
      filters_visible: newValue,
    });
  };

  const handleFilterChange = (resourceIds: string[]) => {
    setSelectedResourceIds(resourceIds);
    saveFiltersMutation.mutate({
      ...savedFilters,
      calendar_ids: resourceIds,
    });
  };

  const handleCollapsedGroupsChange = (
    newCollapsed: FilterGroupCollapsedState
  ) => {
    setCollapsedGroups(newCollapsed);
    saveSettingsMutation.mutate({
      ...savedSettings,
      collapsed_groups: newCollapsed,
    });
  };

  const handleViewChange = (newView: BellvileDentalView) => {
    setView(newView);
    saveSettingsMutation.mutate({
      ...savedSettings,
      default_view: newView,
    });
  };

  const handleEventClick = (event: CalendarEvent) => {
    message.info(`Termin: ${event.title}`);
    // TODO: Open event details modal
  };

  const handleSlotClick = (date: Date, _hour: number) => {
    message.info(`Kreiraj novi termin: ${dayjs(date).format('HH:mm')}`);
    // TODO: Open create event modal
  };

  const handleDayClick = (date: Date) => {
    setCurrentDate(date);
    setView('1day');
  };

  const handleDatePickerChange = (date: Dayjs | null) => {
    if (date) {
      setCurrentDate(date.toDate());
    }
  };

  // Show loading state
  if (loadingCalendars) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Show error if no calendars found
  if (!calendarId) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Kalendar nije pronađen"
          description="Molimo kreirajte kalendar prvo da biste mogli zakazivati termine."
          type="warning"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="bellviledental-calendar-page">
      {/* Calendar Header */}
      <div className="calendar-header">
        <div className="calendar-header-left">
          <Space>
            <Button onClick={handleToday} icon={<CalendarOutlined />}>
              Danas
            </Button>
            <Button.Group>
              <Button icon={<LeftOutlined />} onClick={handlePrevious} />
              <Button icon={<RightOutlined />} onClick={handleNext} />
            </Button.Group>
            <Tooltip
              title={generateFilterSummary(selectedResourceIds, allResources)}
              placement="bottom"
              color="rgba(0, 0, 0, 0.85)"
            >
              <Badge count={selectedResourceIds.length} offset={[-5, 5]}>
                <Button
                  icon={<FilterOutlined />}
                  onClick={handleToggleFilters}
                  type={filtersVisible ? 'primary' : 'default'}
                >
                  Filteri
                </Button>
              </Badge>
            </Tooltip>
          </Space>
        </div>

        <div className="calendar-header-center">
          <h2 className="calendar-period">{formatPeriod(currentDate, view)}</h2>
        </div>

        <div className="calendar-header-right">
          <Space>
            <DatePicker
              value={dayjs(currentDate)}
              onChange={handleDatePickerChange}
              allowClear={false}
              format="D. MMM YYYY"
            />
            <Select
              value={view}
              onChange={handleViewChange}
              style={{ width: 200 }}
              options={[
                { value: '1day', label: '1 Dan (vertikalno)' },
                { value: '2day', label: '2 Dana (vertikalno)' },
                { value: '3day', label: '3 Dana (vertikalno)' },
                { value: '7day', label: '7 Dana (vertikalno)' },
                { value: 'week-new', label: 'Nedelja (Pon-Ned)' },
                { value: 'day-h-1h', label: '1 Dan (horizontalno)' },
                { value: 'day-h-2d', label: '2 Dana (horizontalno)' },
                { value: 'day-h-3d', label: '3 Dana (horizontalno)' },
                { value: 'week-h', label: '7 Dana (horizontalno)' },
                { value: 'month', label: 'Mesec' },
                { value: 'year', label: 'Godina' },
                { value: 'year-agenda', label: 'Godišnja Agenda' },
              ]}
            />
          </Space>
        </div>
      </div>

      <div className="calendar-body">
        {filtersVisible && (
          <GenericResourceFilterPanel
            config={config}
            selectedResourceIds={selectedResourceIds}
            onSelectionChange={handleFilterChange}
            currentView={view}
            events={allEventsForCounting}
            collapsedGroups={collapsedGroups}
            onCollapsedGroupsChange={handleCollapsedGroupsChange}
            dateRange={eventCountDateRange}
          />
        )}

        <div className="calendar-content">
          {eventsError && (
            <Alert
              message="Greška pri učitavanju termina"
              description="Nije moguće učitati termine kalendara. Molimo pokušajte ponovo."
              type="error"
              showIcon
              style={{ margin: 16 }}
            />
          )}

          {view === '1day' && (
            <MultiDayVerticalView
              currentDate={currentDate}
              dayOffset={0}
              dayCount={1}
              events={events}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              onDateChange={setCurrentDate}
              showDayNight={false}
              showWeather={false}
            />
          )}

          {view === '2day' && (
            <MultiDayVerticalView
              currentDate={currentDate}
              dayOffset={0}
              dayCount={2}
              events={events}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              onDateChange={setCurrentDate}
              showDayNight={false}
              showWeather={false}
            />
          )}

          {view === '3day' && (
            <MultiDayVerticalView
              currentDate={currentDate}
              dayOffset={-1}
              dayCount={3}
              events={events}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              onDateChange={setCurrentDate}
              showDayNight={false}
              showWeather={false}
            />
          )}

          {view === '7day' && (
            <MultiDayVerticalView
              currentDate={currentDate}
              dayOffset={-1}
              dayCount={7}
              events={events}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              onDateChange={setCurrentDate}
              showDayNight={false}
              showWeather={false}
            />
          )}

          {view === 'week-new' && (
            <MultiDayVerticalView
              currentDate={currentDate}
              dayOffset={calculateMondayOffset(currentDate)}
              dayCount={7}
              events={events}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              onDateChange={setCurrentDate}
              showDayNight={false}
              showWeather={false}
            />
          )}

          {view === 'month' && (
            <MonthlyView
              currentDate={currentDate}
              events={events}
              loading={loading}
              onDayClick={handleDayClick}
              onEventClick={handleEventClick}
              showDayNight={false}
            />
          )}

          {view === 'day-h-1h' && (
            <HorizontalView
              currentDate={currentDate}
              events={events}
              numDays={1}
              hourResolution={1}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              onDateChange={setCurrentDate}
              selectedResourceIds={selectedResourceIds}
              allResources={allResources}
              showDayNight={false}
              showWeather={false}
            />
          )}

          {view === 'day-h-2d' && (
            <HorizontalView
              currentDate={currentDate}
              events={events}
              numDays={2}
              hourResolution={1}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              selectedResourceIds={selectedResourceIds}
              allResources={allResources}
              showDayNight={false}
              showWeather={false}
            />
          )}

          {view === 'day-h-3d' && (
            <HorizontalView
              currentDate={currentDate}
              events={events}
              numDays={3}
              hourResolution={2}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              selectedResourceIds={selectedResourceIds}
              allResources={allResources}
              showDayNight={false}
              showWeather={false}
            />
          )}

          {view === 'week-h' && (
            <HorizontalView
              currentDate={currentDate}
              events={events}
              numDays={7}
              hourResolution={4}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              selectedResourceIds={selectedResourceIds}
              allResources={allResources}
              showDayNight={false}
              showWeather={false}
            />
          )}

          {view === 'year' && (
            <YearView
              year={currentDate.getFullYear()}
              calendarId={calendarId}
              selectedResourceIds={selectedResourceIds}
              allResources={allResources}
              onDayClick={handleDayClick}
              onMonthClick={(date) => {
                setCurrentDate(date);
                setView('month');
              }}
            />
          )}

          {view === 'year-agenda' && (
            <YearlyAgendaView
              year={currentDate.getFullYear()}
              calendarId={calendarId}
              selectedResourceIds={selectedResourceIds}
              allResources={allResources}
              onEventClick={(event) => {
                console.log('Event clicked:', event);
              }}
              onDayClick={handleDayClick}
            />
          )}
        </div>
      </div>
    </div>
  );
};
